// Tenant SMTP email transport client — /api/v1/integrations/email/config…
//
// Both this and `platform-email.service.ts` satisfy `EmailTransportClient`, so
// the shared components in `components/email/` work against either scope.

import { apiClient } from '@/lib/api-client';
import type {
  EmailTransportConfigMasked,
  EmailVerificationOutcome,
  TestEmailResult,
  UpsertEmailTransportInput,
  UpsertEmailTransportResult,
  VerifyEmailTransportResult,
} from '@bbpos/types';

/**
 * The six operations of plan §8, identical for both scopes.
 *
 * `resendVerification` is typed as `EmailVerificationOutcome` rather than
 * `ResendEmailVerificationResult` because that is what the controllers return:
 * a resend whose SMTP handshake fails comes back `{ sent: false, error }` with
 * a 200, not an exception. `ResendEmailVerificationResult` is assignable to it.
 */
export interface EmailTransportClient {
  getConfig(): Promise<EmailTransportConfigMasked>;
  upsertConfig(input: UpsertEmailTransportInput): Promise<UpsertEmailTransportResult>;
  verify(code: string): Promise<VerifyEmailTransportResult>;
  resendVerification(): Promise<EmailVerificationOutcome>;
  sendTest(to?: string): Promise<TestEmailResult>;
  disableConfig(): Promise<{ disabled: boolean }>;
}

const BASE = '/integrations/email/config';

export const emailTransportService: EmailTransportClient = {
  getConfig() {
    return apiClient<EmailTransportConfigMasked>(BASE);
  },

  upsertConfig(input: UpsertEmailTransportInput) {
    return apiClient<UpsertEmailTransportResult>(BASE, { method: 'PUT', body: input });
  },

  verify(code: string) {
    return apiClient<VerifyEmailTransportResult>(`${BASE}/verify`, {
      method: 'POST',
      body: { code },
    });
  },

  resendVerification() {
    return apiClient<EmailVerificationOutcome>(`${BASE}/verify/resend`, { method: 'POST' });
  },

  sendTest(to?: string) {
    return apiClient<TestEmailResult>(`${BASE}/test`, {
      method: 'POST',
      body: to ? { to } : {},
    });
  },

  disableConfig() {
    return apiClient<{ disabled: boolean }>(BASE, { method: 'DELETE' });
  },
};
